import React from 'react'
import { useQuery } from 'react-relay-offline'
import { graphql } from 'babel-plugin-relay/macro'
import { Text, csx } from '@vtex/admin-ui'
import { FormattedMessage } from 'react-intl'
import { Redirect, useHistory } from 'react-router'

import { SalesAssociatesHeader } from './components/SalesAssociatesList/SalesAssociatesHeader'
import { SalesAssociatesCardsSkeleton } from './components/SalesAssociatesList/SalesAssociatesCardsSkeleton'
import { SalesAssociatesContent } from './components/SalesAssociatesList/SalesAssociatesContent'
import { SalesAssociateCard } from './components/SalesAssociatesList/SalesAssociateCard'
import { SalesAssociateCardName } from './components/SalesAssociatesList/SalesAssociateCardName'
import { SalesAssociateCardAvatar } from './components/SalesAssociatesList/SalesAssociateCardAvatar'
import { getNameInitials } from './utils/getNameInitials'
import type { SalesAssociatesScreenQuery } from './__generated__/SalesAssociatesScreenQuery.graphql'
import { ReadableMessageError } from '../../../../core/screens/GenericError/ReadableMessageError'
import { useSalesAssociate } from '../../../../core/globalState'
import { TopbarLeftArea } from '../../../Instore/components/Header/Topbar'

export const BASE_URL = 'sales-performance'

export const SalesAssociatesScreen = () => {
  const { salesAssociate: currentSalesAssociate } = useSalesAssociate()
  const history = useHistory()

  const { data, isLoading } = useQuery<SalesAssociatesScreenQuery>(
    graphql`
      query SalesAssociatesScreenQuery($masterdataId: String!) {
        store(where: { masterdataId: { eq: $masterdataId } }) {
          id
          salesAssociates(first: 100, where: { active: { eq: true } }) {
            nodes {
              id
              fullName
            }
          }
        }
        viewer {
          configuration {
            viewOthersSalesAssociatesPerformance
          }
          salesAssociate {
            id
          }
        }
      }
    `,
    { masterdataId: currentSalesAssociate?.store ?? '' },
    {
      fetchPolicy: 'store-and-network',
      networkCacheConfig: {
        ttl: 7 * 24 * 60 * 60 * 1000 /* 7 days */,
      },
    }
  )

  if (!isLoading) {
    if (!data?.store)
      throw new ReadableMessageError(
        'disabled-functionality',
        'store-not-found'
      )

    if (data?.store.salesAssociates.nodes.length === 0)
      throw new ReadableMessageError(
        'disabled-functionality',
        'sales-associates-not-found'
      )
  }

  if (
    data &&
    data.viewer.configuration.viewOthersSalesAssociatesPerformance === false
  ) {
    return (
      <Redirect
        to={`${BASE_URL}/${data?.store?.id}/${data?.viewer.salesAssociate?.id}`}
      />
    )
  }

  const handleSelectSalesAssociate = (id: string) => {
    history.push(`${BASE_URL}/${data?.store?.id}/${id}`)
  }

  return (
    <>
      <TopbarLeftArea>
        <Text variant="title2">
          <FormattedMessage id="menu-sales-performance" />
        </Text>
      </TopbarLeftArea>
      <div className={csx({ display: 'flex', flexDirection: 'column' })}>
        <SalesAssociatesHeader>
          <FormattedMessage id="sales-performance.who-are-you" />
        </SalesAssociatesHeader>
        <SalesAssociatesContent>
          {isLoading && !data ? (
            <SalesAssociatesCardsSkeleton />
          ) : (
            data?.store?.salesAssociates.nodes.map((salesAssociate) => (
              <SalesAssociateCard
                key={salesAssociate.id}
                onClick={() => handleSelectSalesAssociate(salesAssociate.id)}
              >
                <SalesAssociateCardName>
                  {salesAssociate.fullName}
                </SalesAssociateCardName>
                <SalesAssociateCardAvatar>
                  {getNameInitials(salesAssociate.fullName ?? '')}
                </SalesAssociateCardAvatar>
              </SalesAssociateCard>
            ))
          )}
        </SalesAssociatesContent>
      </div>
    </>
  )
}
